import { slugify } from '../ids.js';
import { CONTENT_SCHEMA_VERSION, type DocumentContent, type RichDoc, type Section } from '../content/types.js';

export interface TemplateFieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'date' | 'select' | 'tags' | 'user';
  required?: boolean;
  options?: string[];
  help?: string;
  defaultValue?: string;
}

export interface BuiltinTemplate {
  key: string;
  documentTypeKey: string;
  name: string;
  description: string;
  referencePrefix: string;
  fields: TemplateFieldDef[];
  content: DocumentContent;
}

export interface BuiltinDocumentType {
  key: string;
  name: string;
  description: string;
  referencePrefix: string;
  sortOrder: number;
}

export const BUILTIN_DOCUMENT_TYPES: BuiltinDocumentType[] = [
  { key: 'policy', name: 'Policy', description: 'A board-level statement of intent and mandatory requirements.', referencePrefix: 'POL', sortOrder: 10 },
  { key: 'supporting_policy', name: 'Supporting Policy', description: 'A topic-specific policy that sits under a parent policy.', referencePrefix: 'SUP', sortOrder: 20 },
  { key: 'standard', name: 'Standard', description: 'Mandatory, measurable technical or process requirements.', referencePrefix: 'STD', sortOrder: 30 },
  { key: 'procedure', name: 'Procedure', description: 'Step-by-step instructions for carrying out an activity.', referencePrefix: 'PRO', sortOrder: 40 },
  { key: 'guideline', name: 'Guideline', description: 'Recommended, non-mandatory good practice.', referencePrefix: 'GDL', sortOrder: 50 },
  { key: 'control', name: 'Control Description', description: 'A description of a specific control and how it is evidenced.', referencePrefix: 'CTL', sortOrder: 60 },
];

function para(text: string): RichDoc['content'][number] {
  return text ? { type: 'paragraph', content: [{ type: 'text', text }] } : { type: 'paragraph' };
}

function bullets(items: string[]) {
  return {
    type: 'bulletList',
    content: items.map((item) => ({
      type: 'listItem',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: item }] }],
    })),
  };
}

function body(nodes: RichDoc['content']): RichDoc {
  return { type: 'doc', content: nodes.length ? nodes : [{ type: 'paragraph' }] };
}

interface SectionSpec {
  title: string;
  guidance?: string;
  locked?: boolean;
  numbered?: boolean;
  nodes?: RichDoc['content'];
  children?: SectionSpec[];
}

function section(spec: SectionSpec, prefix: string): Section {
  const anchor = slugify(spec.title);
  return {
    id: `${prefix}-${anchor}`,
    title: spec.title,
    anchor,
    numbered: spec.numbered !== false,
    ...(spec.guidance ? { guidance: spec.guidance } : {}),
    ...(spec.locked ? { locked: true } : {}),
    body: body(spec.nodes ?? []),
    children: (spec.children ?? []).map((child) => section(child, prefix)),
  };
}

function content(prefix: string, specs: SectionSpec[]): DocumentContent {
  return { schemaVersion: CONTENT_SCHEMA_VERSION, sections: specs.map((spec) => section(spec, prefix)) };
}

const COMMON_FIELDS: TemplateFieldDef[] = [
  { key: 'owner', label: 'Document owner', type: 'text', required: true, help: 'Role accountable for keeping this document current.' },
  { key: 'approver', label: 'Approver', type: 'text', help: 'Body or role that formally approves this document.' },
  {
    key: 'classification',
    label: 'Classification',
    type: 'select',
    options: ['public', 'internal', 'confidential', 'restricted'],
    defaultValue: 'internal',
    required: true,
  },
  { key: 'effective_from', label: 'Effective from', type: 'date' },
  { key: 'review_due', label: 'Next review due', type: 'date', help: 'Most policies are reviewed at least annually.' },
  { key: 'tags', label: 'Tags', type: 'tags', help: 'Frameworks or themes, e.g. iso27001, gdpr.' },
];

const DOC_CONTROL: SectionSpec = {
  title: 'Document control',
  numbered: false,
  locked: true,
  guidance: 'Version history is maintained automatically. Use this section for anything the register does not capture.',
  nodes: [para('Version history, approval records and distribution are maintained in the policy register.')],
};

export const BUILTIN_TEMPLATES: BuiltinTemplate[] = [
  {
    key: 'policy',
    documentTypeKey: 'policy',
    name: 'Policy',
    description: 'Board-level policy with purpose, scope, principles, roles and compliance.',
    referencePrefix: 'POL',
    fields: COMMON_FIELDS,
    content: content('tpl-policy', [
      {
        title: 'Purpose',
        locked: true,
        guidance: 'One or two paragraphs: why this policy exists and what outcome it protects.',
        nodes: [para('This policy sets out the organisation’s commitment to …')],
      },
      {
        title: 'Scope',
        locked: true,
        guidance: 'Who and what the policy applies to, and any explicit exclusions.',
        nodes: [
          para('This policy applies to:'),
          bullets(['All employees, contractors and temporary staff', 'All third parties acting on behalf of the organisation', 'All information systems and data owned or operated by the organisation']),
        ],
      },
      {
        title: 'Policy statement',
        locked: true,
        guidance: 'The mandatory requirements. Use "must" for obligations and "should" for recommendations.',
        children: [
          { title: 'Principles', nodes: [para('The organisation must …')] },
          { title: 'Requirements', nodes: [para('Add the specific, testable requirements here.')] },
        ],
      },
      {
        title: 'Roles and responsibilities',
        guidance: 'Name the accountable role, not the person.',
        nodes: [
          {
            type: 'table',
            content: [
              {
                type: 'tableRow',
                content: [
                  { type: 'tableHeader', content: [para('Role')] },
                  { type: 'tableHeader', content: [para('Responsibility')] },
                ],
              },
              {
                type: 'tableRow',
                content: [
                  { type: 'tableCell', content: [para('Board')] },
                  { type: 'tableCell', content: [para('Approves this policy and holds management to account.')] },
                ],
              },
              {
                type: 'tableRow',
                content: [
                  { type: 'tableCell', content: [para('Policy owner')] },
                  { type: 'tableCell', content: [para('Maintains the policy and reports on its effectiveness.')] },
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Compliance and exceptions',
        guidance: 'How compliance is measured and how an exception is requested and approved.',
        nodes: [para('Compliance with this policy is mandatory. Exceptions must be requested in writing and approved by the policy owner.')],
      },
      {
        title: 'Related documents',
        guidance: 'Use the Cross reference button to link to other documents so links stay valid when they are renamed.',
        nodes: [para('')],
      },
      { title: 'Definitions', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
  {
    key: 'supporting_policy',
    documentTypeKey: 'supporting_policy',
    name: 'Supporting Policy',
    description: 'Topic-specific policy that inherits scope from a parent policy.',
    referencePrefix: 'SUP',
    fields: [
      ...COMMON_FIELDS,
      { key: 'parent_policy', label: 'Parent policy', type: 'text', help: 'Reference of the policy this supports, e.g. POL-001.' },
    ],
    content: content('tpl-sup', [
      {
        title: 'Purpose',
        locked: true,
        nodes: [para('This supporting policy expands on the requirements of the parent policy in respect of …')],
      },
      {
        title: 'Relationship to parent policy',
        locked: true,
        guidance: 'Insert a cross reference to the parent policy.',
        nodes: [para('This document supports and must be read alongside the parent policy.')],
      },
      { title: 'Scope', locked: true, nodes: [para('This supporting policy applies to …')] },
      {
        title: 'Requirements',
        locked: true,
        children: [{ title: 'Mandatory requirements', nodes: [para('Personnel must …')] }, { title: 'Recommended practice', nodes: [para('Personnel should …')] }],
      },
      { title: 'Monitoring and reporting', nodes: [para('')] },
      { title: 'Exceptions', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
  {
    key: 'standard',
    documentTypeKey: 'standard',
    name: 'Standard',
    description: 'Mandatory, measurable requirements with control mappings.',
    referencePrefix: 'STD',
    fields: [
      ...COMMON_FIELDS,
      { key: 'framework', label: 'Framework', type: 'select', options: ['ISO 27001', 'NIST CSF', 'SOC 2', 'Cyber Essentials', 'PCI DSS', 'Other'], help: 'Primary framework this standard supports.' },
    ],
    content: content('tpl-std', [
      { title: 'Purpose', locked: true, nodes: [para('This standard defines the minimum requirements for …')] },
      { title: 'Scope', locked: true, nodes: [para('This standard applies to …')] },
      {
        title: 'Requirements',
        locked: true,
        guidance: 'Each requirement should be uniquely identifiable, testable and unambiguous.',
        children: [
          { title: 'Baseline configuration', nodes: [para('Systems must …')] },
          { title: 'Access control', nodes: [para('Access must …')] },
          { title: 'Logging and monitoring', nodes: [para('Systems must …')] },
        ],
      },
      {
        title: 'Control mapping',
        guidance: 'Map each requirement to the framework control it satisfies.',
        nodes: [
          {
            type: 'table',
            content: [
              {
                type: 'tableRow',
                content: [
                  { type: 'tableHeader', content: [para('Requirement')] },
                  { type: 'tableHeader', content: [para('Framework control')] },
                  { type: 'tableHeader', content: [para('Evidence')] },
                ],
              },
              {
                type: 'tableRow',
                content: [
                  { type: 'tableCell', content: [para('')] },
                  { type: 'tableCell', content: [para('')] },
                  { type: 'tableCell', content: [para('')] },
                ],
              },
            ],
          },
        ],
      },
      { title: 'Compliance measurement', nodes: [para('Compliance is assessed by …')] },
      { title: 'Exceptions', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
  {
    key: 'procedure',
    documentTypeKey: 'procedure',
    name: 'Procedure',
    description: 'Step-by-step operational instructions with inputs, steps and records.',
    referencePrefix: 'PRO',
    fields: [
      ...COMMON_FIELDS,
      { key: 'frequency', label: 'Frequency', type: 'text', help: 'How often this procedure is performed, e.g. on demand, monthly.' },
    ],
    content: content('tpl-pro', [
      { title: 'Purpose', locked: true, nodes: [para('This procedure describes how to …')] },
      { title: 'Scope and trigger', locked: true, nodes: [para('This procedure is triggered when …')] },
      { title: 'Prerequisites', nodes: [bullets(['Access to …', 'Approval from …'])] },
      {
        title: 'Procedure steps',
        locked: true,
        guidance: 'Use a numbered list. One action per step, with the responsible role named.',
        nodes: [
          {
            type: 'orderedList',
            content: [
              { type: 'listItem', content: [para('Record the request in the service desk.')] },
              { type: 'listItem', content: [para('Verify the requester’s identity.')] },
              { type: 'listItem', content: [para('Complete the action and record the outcome.')] },
            ],
          },
        ],
      },
      { title: 'Records', guidance: 'What evidence this procedure produces and where it is retained.', nodes: [para('')] },
      { title: 'Escalation', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
  {
    key: 'guideline',
    documentTypeKey: 'guideline',
    name: 'Guideline',
    description: 'Recommended, non-mandatory good practice.',
    referencePrefix: 'GDL',
    fields: COMMON_FIELDS,
    content: content('tpl-gdl', [
      { title: 'Purpose', locked: true, nodes: [para('This guideline offers recommended practice for …')] },
      { title: 'Audience', nodes: [para('')] },
      { title: 'Recommendations', locked: true, nodes: [bullets(['Consider …', 'Where practical, …'])] },
      { title: 'Further reading', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
  {
    key: 'control',
    documentTypeKey: 'control',
    name: 'Control Description',
    description: 'A single control, its design, operation and evidence.',
    referencePrefix: 'CTL',
    fields: [
      ...COMMON_FIELDS,
      { key: 'control_type', label: 'Control type', type: 'select', options: ['Preventive', 'Detective', 'Corrective', 'Directive'] },
      { key: 'automation', label: 'Automation', type: 'select', options: ['Manual', 'Semi-automated', 'Automated'] },
    ],
    content: content('tpl-ctl', [
      { title: 'Control objective', locked: true, nodes: [para('')] },
      { title: 'Control description', locked: true, nodes: [para('')] },
      { title: 'Control owner', nodes: [para('')] },
      { title: 'Operating frequency', nodes: [para('')] },
      { title: 'Evidence of operation', locked: true, nodes: [para('')] },
      { title: 'Testing approach', nodes: [para('')] },
      DOC_CONTROL,
    ]),
  },
];

export function findBuiltinTemplate(key: string): BuiltinTemplate | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.key === key);
}
